import { Injectable, computed, effect, inject, linkedSignal, resource, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ExecutionPlanService } from '@app/shared/services/execution-plan/execution-plan.service';
import { ProjectService } from '@app/shared/services/project/project.service';
import { PermissionsService } from '@app/shared/services/permissions/permissions.service';
import { WorkflowService } from '@app/shared/services/workflow/workflow.service';
import { CapabilityGrantService } from '@app/shared/services/capability-grant/capability-grant.service';
import type {
  AccessScope,
  CreateGrantRequest,
  GrantPermission,
  GrantTarget,
  MyVisibility,
  ProjectCapabilityGrant,
} from '@app/shared/interfaces/capability-grant.interface';
import type { ProjectUser } from '@app/shared/interfaces/project.interface';
import type { Routine } from '@app/shared/interfaces/routine.interface';
import type { Workflow } from '@app/shared/interfaces/workflow.interface';
import {
  bulkKey,
  grantKey,
  scopeKey,
  toMemberAccessList,
  toRoleBaselines,
  type GrantCell,
  type MemberAccess,
  type RoleBaseline,
} from './project-access.mapper';

/** A failed mutation, tagged with the member whose panel should report it. */
interface MutationFailure {
  userId: number;
  message: string;
}

/**
 * State for the project Access tab (EHQ-187).
 *
 * Three resources rather than one, so a mutation refetches only what it changed:
 * toggling a grant does not re-read the member list, and changing a scope does
 * not re-read the routine catalog. A single combined resource would blank and
 * refill the whole panel on every checkbox.
 *
 * The component is a thin renderer over this — it forwards events and binds
 * signals, and holds no state of its own.
 */
@Injectable()
export class ProjectAccessStore {
  readonly #projects = inject(ProjectService);
  readonly #plans = inject(ExecutionPlanService);
  readonly #workflows = inject(WorkflowService);
  readonly #permissions = inject(PermissionsService);
  readonly #grants = inject(CapabilityGrantService);

  // ── Inputs, set by the component ────────────────────────────────────────

  readonly projectId = signal<number | null>(null);
  readonly versionId = signal<number | null>(null);
  /**
   * Whether the viewer may configure other members' access. When false the tab
   * renders the operator's own read-only view, which reads different endpoints —
   * the admin ones would 403.
   */
  readonly canAdminister = signal(false);

  // ── Refetch triggers ────────────────────────────────────────────────────

  readonly #membersTick = signal(0);
  readonly #grantsTick = signal(0);

  // ── Resources ───────────────────────────────────────────────────────────

  /**
   * The resources a grant can point at, plus the project owner.
   *
   * Loaded once per project/version: routines and workflows do not change while
   * an admin is ticking checkboxes, and the owner is needed to know whom the
   * server will refuse to restrict.
   */
  readonly #catalog = resource({
    params: () => {
      const projectId = this.projectId();
      const versionId = this.versionId();
      return projectId === null ? undefined : { projectId, versionId };
    },
    loader: async ({ params }): Promise<{
      routines: Routine[];
      workflows: Workflow[];
      ownerUserId: number;
      baselines: Map<number, RoleBaseline>;
    }> => {
      const [project, routines, workflows, roles] = await Promise.all([
        firstValueFrom(this.#projects.getProject(params.projectId)),
        firstValueFrom(this.#plans.getPlans(params.projectId)) as Promise<Routine[]>,
        // Workflows are version-scoped. With no version resolved there is nothing
        // to grant on — an empty list is correct, not an error.
        params.versionId === null
          ? Promise.resolve([] as Workflow[])
          : firstValueFrom(this.#workflows.getAllWorkflows(params.projectId, params.versionId)),
        // Which capabilities each role already confers, so the panel does not
        // offer a grant that would be inert. Through PermissionsService rather
        // than a bare GET: it caches across the session, retries with backoff,
        // and handles the unauthenticated case — none of which this tab should
        // reimplement to open a settings dialog.
        firstValueFrom(this.#permissions.getRoles()),
      ]);
      return {
        routines,
        workflows,
        ownerUserId: project.userId,
        baselines: toRoleBaselines(roles),
      };
    },
  });

  /** The project's memberships, carrying each one's two scope axes. */
  readonly #members = resource({
    params: () => {
      const projectId = this.projectId();
      if (projectId === null || !this.canAdminister()) {
        return undefined;
      }
      return { projectId, tick: this.#membersTick() };
    },
    loader: ({ params }): Promise<ProjectUser[]> =>
      firstValueFrom(this.#projects.getProjectUsers(params.projectId)),
  });

  /**
   * Grant rows — every member's for an admin, the viewer's own otherwise.
   *
   * The two cases share a resource because they produce the same shape and are
   * consumed by the same mapper; only the endpoint differs, and which one is
   * legal for the caller is exactly what `canAdminister` decides.
   */
  readonly #grantRows = resource({
    params: () => {
      const projectId = this.projectId();
      if (projectId === null) {
        return undefined;
      }
      return { projectId, mine: !this.canAdminister(), tick: this.#grantsTick() };
    },
    loader: ({ params }): Promise<ProjectCapabilityGrant[]> =>
      params.mine ? this.#grants.listMine(params.projectId) : this.#grants.list(params.projectId),
  });

  /** The viewer's own effective visibility, for the operator read-only view. */
  readonly #visibility = resource({
    params: () => {
      const projectId = this.projectId();
      if (projectId === null || this.canAdminister()) {
        return undefined;
      }
      return { projectId, tick: this.#grantsTick() };
    },
    loader: ({ params }): Promise<MyVisibility> => this.#grants.myVisibility(params.projectId),
  });

  // ── Derived state ───────────────────────────────────────────────────────
  //
  // Every read goes through `hasValue()`. A `resource`'s `value()` *throws* while
  // the resource is in its error state, so reading it unguarded turns a failed
  // request into a template render error rather than the error banner below.

  readonly #catalogValue = computed(() =>
    this.#catalog.hasValue() ? this.#catalog.value() : undefined
  );
  readonly #membersValue = computed(() =>
    this.#members.hasValue() ? this.#members.value() : undefined
  );
  readonly #grantsValue = computed(() =>
    this.#grantRows.hasValue() ? this.#grantRows.value() : undefined
  );

  readonly routines = computed<Routine[]>(() => this.#catalogValue()?.routines ?? []);
  readonly workflows = computed<Workflow[]>(() => this.#catalogValue()?.workflows ?? []);
  readonly visibility = computed<MyVisibility | undefined>(() =>
    this.#visibility.hasValue() ? this.#visibility.value() : undefined
  );

  /** One row per member, or `undefined` until every input has loaded once. */
  readonly #memberRows = computed<MemberAccess[] | undefined>(() => {
    const catalog = this.#catalogValue();
    const members = this.#membersValue();
    const grants = this.#grantsValue();
    if (!catalog || !members || !grants) {
      return undefined;
    }
    return toMemberAccessList(
      members,
      grants,
      catalog.routines,
      catalog.workflows,
      catalog.ownerUserId,
      catalog.baselines
    );
  });

  /**
   * One row per member, with every grantable cell resolved. Admin view only.
   *
   * `linkedSignal` rather than a plain `computed` so the rows survive a refetch.
   * A resource has no value while it reloads, so a `computed` would return an
   * empty list for the duration of every save — emptying the `@for`, destroying
   * every row, and taking the expanded member's panel with it. The panel would
   * then rebuild from scratch a moment later, which reads as the whole tab
   * repainting on each checkbox. Holding the previous rows means a save updates
   * the one cell that changed.
   */
  readonly members = linkedSignal<MemberAccess[] | undefined, MemberAccess[]>({
    source: () => this.#memberRows(),
    computation: (value, previous) => value ?? previous?.value ?? [],
  });

  /**
   * The viewer's own live grants, for the read-only view.
   *
   * Revoked rows are dropped: this answers "what may I do", and a withdrawn
   * grant is not an answer to that.
   */
  readonly myGrants = computed<ProjectCapabilityGrant[]>(() =>
    (this.#grantsValue() ?? []).filter((grant) => !grant.disabled)
  );

  /** Whether any request is in flight, including a refetch behind rendered content. */
  readonly isLoading = computed(
    () =>
      this.#catalog.isLoading() ||
      this.#members.isLoading() ||
      this.#grantRows.isLoading() ||
      this.#visibility.isLoading()
  );

  /**
   * Whether anything has ever loaded. Latches true and stays there.
   *
   * Refetches drop each resource's value while they run, so without the latch
   * "we have content" would flip false on every save — which is exactly the
   * repaint this exists to prevent. A plain `signal` set from an `effect` rather
   * than a `linkedSignal` because the latch has to survive its source going
   * false, and that is easier to state than to encode in a computation.
   */
  readonly #hasContent = signal(false);

  /**
   * Whether to show the loading placeholder *instead of* the panel.
   *
   * Only true before anything has rendered. Gating the panel on plain
   * {@link isLoading} swaps the whole tab for a spinner during every save —
   * which is a heavier repaint than the one `linkedSignal` was added to prevent,
   * and it hides content the store still holds. A refetch behind existing
   * content is invisible by design.
   */
  readonly isInitialLoading = computed(() => this.isLoading() && !this.#hasContent());

  /**
   * Whether a load failure should replace the panel rather than sit above it.
   *
   * A failed *refetch* leaves the last good rows on screen with a banner — the
   * data is stale, not gone, and blanking a panel the admin was working in loses
   * more than it protects.
   */
  readonly isBlockingError = computed(() => this.error() !== undefined && !this.#hasContent());

  readonly error = computed(
    () =>
      this.#catalog.error() ??
      this.#members.error() ??
      this.#grantRows.error() ??
      this.#visibility.error()
  );

  constructor() {
    // Latch as soon as either view has something to show. Never cleared: once
    // the panel has rendered, a later refetch is a background refresh, not a
    // reason to replace it with a spinner.
    effect(() => {
      const loaded =
        this.#memberRows() !== undefined ||
        (this.#visibility.hasValue() && this.#visibility.value() !== undefined);
      if (loaded && !this.#hasContent()) {
        this.#hasContent.set(true);
      }
    });
  }

  /**
   * The controls with a mutation in flight, by key.
   *
   * A set rather than one boolean because a single flag is bound to every
   * control's `[disabled]`, so clicking one checkbox re-evaluates all of them
   * and the whole panel visibly reacts to a change in one cell. Keys come from
   * {@link grantKey} and {@link scopeKey}, so a control disables itself and
   * nothing else — and two edits to different rows can be in flight at once.
   */
  readonly #pending = signal<ReadonlySet<string>>(new Set());

  /** Whether this specific control has a mutation in flight. */
  isPending(key: string): boolean {
    return this.#pending().has(key);
  }

  /** Whether any mutation is in flight. For a spinner, never for `[disabled]`. */
  readonly isSaving = computed(() => this.#pending().size > 0);

  /**
   * Mutation failures, keyed the same way {@link isPending} is.
   *
   * One unkeyed message would not do. `#pending` is a keyed set precisely so two
   * edits on different rows can be in flight at once, and under that concurrency
   * a single message cannot be attributed to the control that produced it — and
   * each new attempt would wipe the previous message before the admin had read
   * it. Keying by control means a failure is reported next to the member it
   * belongs to, and only that member's message is cleared when they retry.
   */
  readonly #saveErrors = signal<ReadonlyMap<string, MutationFailure>>(new Map());

  /** Failure messages for one member's controls, newest last. */
  messagesFor(userId: number): { key: string; message: string }[] {
    return [...this.#saveErrors().entries()]
      .filter(([, failure]) => failure.userId === userId)
      .map(([key, failure]) => ({ key, message: failure.message }));
  }

  /** Whether any control currently reports a failure. */
  readonly hasSaveError = computed(() => this.#saveErrors().size > 0);

  // ── Mutations ───────────────────────────────────────────────────────────

  /**
   * Grants or revokes one capability on one resource.
   *
   * Takes the current cell rather than a boolean because revoking needs the
   * grant's id, which only the cell knows — deriving it again here would mean a
   * second scan of the rows the mapper already resolved.
   */
  async toggleGrant(
    userId: number,
    permission: GrantPermission,
    target: GrantTarget,
    cell: GrantCell
  ): Promise<boolean> {
    const projectId = this.projectId();
    if (projectId === null) {
      return false;
    }
    // An implied cell has no row of its own — its authority comes from the edit
    // grant, and there is nothing here to revoke. The component disables these,
    // so reaching this is a bug rather than a user action.
    if (cell.implied) {
      return false;
    }

    return this.#mutate(
      grantKey(userId, permission, target),
      userId,
      () => this.#grantsTick.update((tick) => tick + 1),
      async () => {
        if (cell.granted && cell.grantId !== undefined) {
          await this.#grants.revoke(projectId, cell.grantId);
        } else {
          await this.#grants.create(projectId, {
            userId,
            permission,
            ...target,
          } as CreateGrantRequest);
        }
      }
    );
  }

  /**
   * Grants every listed capability at once — what check-all sends.
   *
   * Already-granted entries are included rather than filtered out: the server's
   * create is an idempotent upsert, so re-issuing one returns the same row, and
   * sending the full set keeps the request a statement of the desired state.
   */
  async grantAll(
    userId: number,
    permission: GrantPermission,
    grants: CreateGrantRequest[]
  ): Promise<boolean> {
    const projectId = this.projectId();
    if (projectId === null || grants.length === 0) {
      return false;
    }
    return this.#mutate(
      bulkKey(userId, permission),
      userId,
      () => this.#grantsTick.update((tick) => tick + 1),
      async () => {
        await this.#grants.createBatch(projectId, grants);
      }
    );
  }

  /**
   * Revokes every listed grant.
   *
   * Sequential rather than concurrent: there is no batch revoke, and firing N
   * deletes at once against one member's rows buys little over a handful of
   * requests while making a partial failure harder to describe.
   */
  async revokeAll(
    userId: number,
    permission: GrantPermission,
    grantIds: number[]
  ): Promise<boolean> {
    const projectId = this.projectId();
    if (projectId === null || grantIds.length === 0) {
      return false;
    }
    return this.#mutate(
      bulkKey(userId, permission),
      userId,
      () => this.#grantsTick.update((tick) => tick + 1),
      async () => {
        for (const grantId of grantIds) {
          await this.#grants.revoke(projectId, grantId);
        }
      }
    );
  }

  /**
   * Sets both of a member's scope axes.
   *
   * Both go together because the server writes them together; sending one would
   * reset the other to whatever this client last believed. That is why the edit
   * also carries the membership version this panel loaded: if another admin
   * changed the member since, the server refuses it rather than undoing their
   * change, and the refetch below shows the panel what they did (EHQ-191).
   */
  async setScopes(
    userId: number,
    viewScope: AccessScope,
    editScope: AccessScope
  ): Promise<boolean> {
    const projectId = this.projectId();
    if (projectId === null) {
      return false;
    }
    return this.#mutate(
      scopeKey(userId),
      userId,
      () => this.#membersTick.update((tick) => tick + 1),
      async () => {
        const version = this.#membersValue()?.find((m) => m.userId === userId)?.version ?? 0;
        await this.#grants.setMemberScopes(projectId, userId, { viewScope, editScope, version });
      }
    );
  }

  /**
   * Runs a mutation with the saving flag and error handling every one of them
   * needs.
   *
   * The error is kept as a message rather than rethrown: every caller is a
   * template event handler, where a rejected promise becomes an unhandled
   * rejection and the user sees nothing.
   */
  async #mutate(
    key: string,
    userId: number,
    refetch: () => void,
    operation: () => Promise<void>
  ): Promise<boolean> {
    this.#pending.update((keys) => new Set(keys).add(key));
    // Clear only this control's previous failure. Clearing all of them would
    // discard a message about a different member that nobody has read yet.
    this.#clearError(key);
    let applied = true;
    try {
      await operation();
    } catch (error: unknown) {
      applied = false;
      const message = this.#messageFor(error);
      this.#saveErrors.update((errors) => new Map(errors).set(key, { userId, message }));
    } finally {
      // Refetch on failure too. A sequence of revokes can fail part-way with
      // some rows already disabled, and skipping the refetch would leave the
      // panel showing authority the server has already taken away.
      refetch();
      this.#pending.update((keys) => {
        const next = new Set(keys);
        next.delete(key);
        return next;
      });
    }
    return applied;
  }

  /** Drops one control's failure message. */
  #clearError(key: string): void {
    this.#saveErrors.update((errors) => {
      if (!errors.has(key)) {
        return errors;
      }
      const next = new Map(errors);
      next.delete(key);
      return next;
    });
  }

  /** The server's explanation when it sent one, since it names the actual rule broken. */
  #messageFor(error: unknown): string {
    const body = (error as { error?: { code?: string; message?: string } } | null)?.error;
    // The server's text ("expected version 3 but current is 4") is accurate
    // but says nothing an admin can act on. The refetch has already reloaded
    // the panel, so tell them to look before trying again.
    if (body?.code === 'VERSION_CONFLICT') {
      return 'Someone else changed this member while you were editing. The panel now shows their change; apply yours again if it is still needed.';
    }
    if (body?.message) {
      return body.message;
    }
    return error instanceof Error ? error.message : 'The change could not be saved.';
  }
}
